import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import '../../style/dashboard.css';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const CallCenterSurveyDashboard = () => {
    const navigate = useNavigate();
    const { appId, total_voters, language } = useAppId();
    
    const lang = language || 'en';

    // Translation dictionary
    const t = {
        en: {
            title: "Call Center Survey Dashboard",
            subtitle: "Overview of call center survey progress and reports.",
            totalVoters: "Total Voters",
            totalSurveyed: "Total Surveyed",
            done: "Done",
            
            totalCall: "Total Call",
            notReceived: "Not Received",
            wrongMobile: "Wrong Mobile",
            positive: "Positive",
            negative: "Negative",
            doubtful: "Doubtful",
            cantSay: "Can't Say",
            
            surveyReport: "SURVEY REPORT",
            
            boothWiseSurvey: "Booth Wise Survey",
            dateWiseSurvey: "Date Wise Survey",
            callCenterSurvey: "Call Center Survey",

            totalView: "Total Survey",
            todayView: "Today's Survey"
        },
        hi: {
            title: "कॉल सेंटर सर्वेक्षण डैशबोर्ड",
            subtitle: "कॉल सेंटर सर्वेक्षण प्रगति और रिपोर्ट का अवलोकन।",
            totalVoters: "कुल मतदाता",
            totalSurveyed: "कुल सर्वेक्षित",
            done: "पूर्ण",
            
            totalCall: "कुल कॉल",
            notReceived: "प्राप्त नहीं हुआ",
            wrongMobile: "गलत मोबाइल",
            positive: "सकारात्मक",
            negative: "नकारात्मक",
            doubtful: "संदिग्ध",
            cantSay: "कह नहीं सकते",
            
            surveyReport: "सर्वेक्षण रिपोर्ट",
            
            boothWiseSurvey: "बूथ वार सर्वेक्षण",
            dateWiseSurvey: "तिथि वार सर्वेक्षण",
            callCenterSurvey: "कॉल सेंटर सर्वेक्षण",

            totalView: "कुल सर्वेक्षण",
            todayView: "आज का सर्वेक्षण"
        },
        gu: {
            title: "કૉલ સેન્ટર સર્વે ડેશબોર્ડ",
            subtitle: "કૉલ સેન્ટર સર્વે પ્રગતિ અને રિપોર્ટ્સની ઝાંખી.",
            totalVoters: "કુલ મતદારો",
            totalSurveyed: "કુલ સર્વેક્ષણ",
            done: "પૂર્ણ",
            
            totalCall: "કુલ કૉલ",
            notReceived: "પ્રાપ્ત થયું નથી",
            wrongMobile: "ખોટો મોબાઈલ",
            positive: "હકારાત્મક",
            negative: "નકારાત્મક",
            doubtful: "શંકાસ્પદ",
            cantSay: "કહી શકતો નથી",
            
            surveyReport: "સર્વે રિપોર્ટ",
            
            boothWiseSurvey: "બૂથ વાઈઝ સર્વે",
            dateWiseSurvey: "તારીખ વાઈઝ સર્વે",
            callCenterSurvey: "કૉલ સેન્ટર સર્વે",

            totalView: "કુલ સર્વેક્ષણ",
            todayView: "આજનો સર્વે"
        }
    };

    const txt = t[lang];
    
    // Initial State
    const [data, setData] = useState({
        totalVoters: 0,
        totalSurveyed: 0,
        totalCall: 0,
        nr: 0,
        wm: 0,
        p: 0,
        n: 0,
        d: 0,
        c: 0,
        pieData: [
            { name: txt.positive, value: 0, color: '#10b981' },
            { name: txt.negative, value: 0, color: '#ef4444' },
            { name: txt.doubtful, value: 0, color: '#f59e0b' },
            { name: txt.cantSay, value: 0, color: '#6366f1' },
            { name: txt.notReceived, value: 0, color: '#94a3b8' },
            { name: txt.wrongMobile, value: 0, color: '#cbd5e1' },
        ],
    });

    // --- Store Raw API Data and View Mode ---
    const [rawData, setRawData] = useState(null);
    const [viewMode, setViewMode] = useState('total'); // 'total' or 'today'

    // Update pieData labels when language changes
    useEffect(() => {
        setData(prev => ({
            ...prev,
            pieData: [
                { name: txt.positive, value: prev.pieData[0]?.value || 0, color: '#10b981' },
                { name: txt.negative, value: prev.pieData[1]?.value || 0, color: '#ef4444' },
                { name: txt.doubtful, value: prev.pieData[2]?.value || 0, color: '#f59e0b' },
                { name: txt.cantSay, value: prev.pieData[3]?.value || 0, color: '#6366f1' },
                { name: txt.notReceived, value: prev.pieData[4]?.value || 0, color: '#94a3b8' },
                { name: txt.wrongMobile, value: prev.pieData[5]?.value || 0, color: '#cbd5e1' },
            ]
        }));
    }, [lang, txt.positive, txt.negative, txt.doubtful, txt.cantSay, txt.notReceived, txt.wrongMobile]);

    // Fetch API Data
    useEffect(() => {
        if (appId) {
            fetchData();
        }
    }, [appId]);

    const fetchData = async () => {
        try {
            const response = await axios.post(`${BASE_URL}/api/dis_admin_call_center_survey_dashboard`, {
                app_id: appId,
            });

            if (response.data.success && response.data.data) {
                // Store both total (result) and today (result2) data
                setRawData({
                    total: response.data.data.result?.[0] || {},
                    today: response.data.data.result2?.[0] || {}
                });
            }
        } catch (error) {
            console.error("Error fetching call center survey dashboard data:", error);
        }
    };

    // --- Update Display Data when View Mode or Raw Data changes ---
    useEffect(() => {
        if (!rawData) return;

        const resData = viewMode === 'total' ? rawData.total : rawData.today;
        
        const totalVoters = total_voters || 0;
        
        // Extract breakdown counts from the active object (P, N, D, C, NR, WM)
        const p = resData.P || 0;
        const n = resData.N || 0;
        const d = resData.D || 0;
        const c = resData.C || 0;
        const nr = resData.NR || 0; 
        const wm = resData.WM || 0; 

        // Calculate Totals
        const totalCall = p + n + d + c + nr + wm;

        setData(prev => ({
            ...prev,
            totalVoters: totalVoters,
            totalSurveyed: totalCall,
            totalCall: totalCall,
            nr: nr,
            wm: wm,
            p: p,
            n: n,
            d: d,
            c: c,
            pieData: [
                { name: txt.positive, value: p, color: '#10b981' },
                { name: txt.negative, value: n, color: '#ef4444' },
                { name: txt.doubtful, value: d, color: '#f59e0b' },
                { name: txt.cantSay, value: c, color: '#6366f1' },
                { name: txt.notReceived, value: nr, color: '#94a3b8' },
                { name: txt.wrongMobile, value: wm, color: '#cbd5e1' },
            ],
        }));
    }, [rawData, viewMode, total_voters]); 

    const handleNavigate = (path) => {
        navigate(path);
    };

    const surveyOptions = [
        { title: txt.boothWiseSurvey, icon: '🏪', onClick: () => { handleNavigate('/dis_booth_wise_call_center_survey_dash') } }, 
        { title: txt.dateWiseSurvey, icon: '📅', onClick: () => { handleNavigate('/dis_date_wise_call_center_survey_dash') } }, 
        { title: txt.callCenterSurvey, icon: '🎧', onClick: () => { handleNavigate('/dis_user_wise_call_center_survey_dash') } }
    ];

    // Custom Label Render
    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
        if (percent === 0) return null; // Don't show labels for 0% to avoid clutter

        const RADIAN = Math.PI / 180;
        const radius = outerRadius + 20;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text x={x} y={y} fill="#334155" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize="12" fontWeight="600">
                {`${(percent * 100).toFixed(1)}%`}
            </text>
        );
    };

    return (
        <div className="app-container dashboard-page">
            <div className="dashboard-content container-fluid p-4">

                <Header title={txt.title}/>

                <div className="row g-4 mb-5 fade-in-up" style={{ animationDelay: '0.2s', marginTop: '10px' }}>

                    {/* --- Left Side: Chart and Detailed Stats --- */}
                    <div className="col-lg-5 col-md-12">
                        <div className="survey-card h-90 d-flex flex-column justify-content-between">

                            {/* --- View Mode Toggle --- */}
                            <div className="d-flex justify-content-center mb-3 pt-1">
                                <div className="toggle-wrapper">
                                    <button 
                                        className={`toggle-btn ${viewMode === 'total' ? 'active' : ''}`}
                                        onClick={() => setViewMode('total')}
                                    >
                                        {txt.totalView}
                                    </button>
                                    <button 
                                        className={`toggle-btn ${viewMode === 'today' ? 'active' : ''}`}
                                        onClick={() => setViewMode('today')}
                                    >
                                        {txt.todayView}
                                    </button>
                                </div>
                            </div>

                            {/* Chart Area */}
                            <div className="chart-wrapper">
                                <ResponsiveContainer width="100%" height={280}>
                                    <PieChart>
                                        <Pie
                                            data={data.pieData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={80}
                                            outerRadius={110}
                                            paddingAngle={0}
                                            dataKey="value"
                                            startAngle={90}
                                            endAngle={-270}
                                            label={renderCustomizedLabel}
                                            labelLine={true}
                                        >
                                            {data.pieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            formatter={(value) => value.toLocaleString()}
                                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>

                                {/* Center Text (Now showing Total Calls) */}
                                <div className="chart-center-text">
                                    <span className="survey-percentage">
                                        {data.totalCall.toLocaleString()}
                                    </span>
                                    <span className="survey-label">{txt.totalCall}</span>
                                </div>
                            </div>

                            {/* Detailed Stats Grid Below Chart */}
                            <div className="detailed-stats-grid">
                                <div className="d-stat-box"><span className="d-stat-val c-total">{data.totalCall.toLocaleString()}</span><span className="d-stat-label">{txt.totalCall}</span></div>
                                <div className="d-stat-box"><span className="d-stat-val c-nr">{data.nr.toLocaleString()}</span><span className="d-stat-label">{txt.notReceived}</span></div>
                                <div className="d-stat-box"><span className="d-stat-val c-wm">{data.wm.toLocaleString()}</span><span className="d-stat-label">{txt.wrongMobile}</span></div>
                                <div className="d-stat-box"><span className="d-stat-val c-p">{data.p.toLocaleString()}</span><span className="d-stat-label">{txt.positive}</span></div>
                                <div className="d-stat-box"><span className="d-stat-val c-n">{data.n.toLocaleString()}</span><span className="d-stat-label">{txt.negative}</span></div>
                                <div className="d-stat-box"><span className="d-stat-val c-d">{data.d.toLocaleString()}</span><span className="d-stat-label">{txt.doubtful}</span></div>
                                <div className="d-stat-box"><span className="d-stat-val c-c">{data.c.toLocaleString()}</span><span className="d-stat-label">{txt.cantSay}</span></div>
                            </div>
                        </div>
                    </div>

                    {/* --- Right Side: Overall Survey Options --- */}
                    <div className="col-lg-7 col-md-12">
                        <div className="survey-card">
                            <div className="pb-4">
                                <h4 className="section-title">{txt.surveyReport}</h4>
                            </div>

                            <div className="row g-3">
                                {surveyOptions.map((option, index) => (
                                    <div key={index} className="col-6 col-md-4">
                                        <div className="option-card" onClick={option.onClick}>
                                            <div className="option-icon">{option.icon}</div>
                                            <span className="option-text">{option.title}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style jsx>{`
                /* Common Card Style */
                .survey-card {
                    background: white;
                    border-radius: 16px;
                    padding: 24px;
                    box-shadow: 0 2px 10px rgba(0,0,0,0.03);
                    border: 1px solid #f1f5f9;
                }

                /* --- NEW PREMIUM TOGGLE BUTTON STYLES --- */
                .toggle-wrapper {
                    display: inline-flex;
                    background: #e2e8f0;
                    border-radius: 30px;
                    padding: 4px;
                    box-shadow: inset 0 2px 4px rgba(0,0,0,0.05);
                }
                .toggle-btn {
                    background: transparent;
                    border: none;
                    padding: 8px 20px;
                    font-size: 13px;
                    font-weight: 700;
                    color: #64748b;
                    border-radius: 26px;
                    cursor: pointer;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .toggle-btn:hover:not(.active) {
                    color: #3b82f6;
                }
                .toggle-btn.active {
                    background: #3b82f6;
                    color: white;
                    box-shadow: 0 4px 10px rgba(59, 130, 246, 0.3);
                }

                .chart-wrapper {
                    position: relative;
                    margin: 10px 0 25px 0;
                }
                .chart-center-text {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    pointer-events: none; 
                }
                .survey-percentage {
                    font-size: 22px;
                    font-weight: 800;
                    color: #0f172a;
                }
                .survey-label {
                    font-size: 12px;
                    color: #64748b;
                    font-weight: 600;
                    margin-top: 4px;
                    text-align: center;
                }

                /* --- STATS GRID --- */
                .detailed-stats-grid {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 10px;
                    justify-content: center;
                    border-top: 1px solid #f1f5f9;
                    padding-top: 20px;
                }
                .d-stat-box {
                    flex: 1 1 20%; /* Makes columns wrap nicely */
                    min-width: 80px;
                    background: #f8fafc;
                    padding: 10px;
                    border-radius: 10px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                }
                .d-stat-val {
                    font-size: 17px;
                    font-weight: 800;
                    margin-bottom: 4px;
                }
                .d-stat-label {
                    font-size: 11px;
                    color: #64748b;
                    font-weight: 600;
                    line-height: 1.2;
                }
                
                /* Colors for specific stats */
                .c-total { color: #3b82f6; }
                .c-nr { color: #94a3b8; }
                .c-wm { color: #64748b; }
                .c-p { color: #10b981; }
                .c-n { color: #ef4444; }
                .c-d { color: #f59e0b; }
                .c-c { color: #6366f1; }

                .section-title {
                    font-size: 14px;
                    font-weight: 700;
                    color: #475569;
                    letter-spacing: 0.5px;
                    text-transform: uppercase;
                    margin: 0;
                }
                
                .option-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 25px 15px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    height: 100%;
                }
                .option-card:hover {
                    border-color: #3b82f6;
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
                    transform: translateY(-2px);
                }
                .option-icon {
                    font-size: 28px;
                    margin-bottom: 12px;
                }
                .option-text {
                    font-size: 13px;
                    font-weight: 600;
                    color: #1e293b;
                    text-align: center;
                    line-height: 1.4;
                }
            `}</style>
        </div>
    );
};

export default CallCenterSurveyDashboard;